import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { JobTitle } from "domain/entities/job-title";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { Logger } from "infra/logger/logger";
import repository from "../../infra/repositories/job-title-repository";

export const useJobTitleByFilter = (filter: BaseInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["jobTitles", filter],
    queryFn: async () => {
      try {
        const result = await repository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useDeleteJobTitleMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobTitleId: string) => {
      if (!jobTitleId) throw new Error("ID inválido para exclusão");
      const result = await repository.delete(jobTitleId);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobTitles"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors?.[0]);
      }
      throw error;
    },
  });
};

export const useCreateJobTitleMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: JobTitle) => {
      const result = await repository.create(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobTitles"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors;
        logger.error(errors?.[0], error.response?.data.metadata);
        const customError = new Error("Erro na criação do cargo");
        (customError as any).errors = errors;
        throw customError;
      }
      throw error;
    },
  });
};

export const useUpdateJobTitleMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: JobTitle) => {
      const result = await repository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobTitles"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors?.[0]);
      }
      throw error;
    },
  });
};
